import { useEffect, useRef, useState } from 'react';
import type { Annotation } from '../../lib/evidence.ts';
import { buttonClass } from '../../components/ui/Button.tsx';

/**
 * 스크린샷 주석 편집기 (CR-003): 빨간 박스 · 화살표 · 마스킹 · 텍스트.
 * 원본 이미지 위에 캔버스로 그리고, 저장 시 적용본 PNG Blob 과 주석 정의를 돌려준다.
 */
export function AnnotationEditor({ imageUrl, initial, onSave, onCancel }: { imageUrl: string; initial: Annotation[]; onSave: (a: Annotation[], blob: Blob) => Promise<void>; onCancel: () => void }) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const imgRef = useRef<HTMLImageElement | null>(null);
  const [tool, setTool] = useState<Annotation['type']>('box');
  const [anns, setAnns] = useState<Annotation[]>(initial);
  const [drag, setDrag] = useState<{ x: number; y: number } | null>(null);
  const [cur, setCur] = useState<Annotation | null>(null);
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  useEffect(() => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => { imgRef.current = img; draw(anns, null); };
    img.onerror = () => setErr('이미지를 불러올 수 없습니다 (CORS)');
    img.src = imageUrl;
  }, [imageUrl]); // eslint-disable-line

  useEffect(() => { draw(anns, cur); }, [anns, cur]); // eslint-disable-line

  function draw(list: Annotation[], extra: Annotation | null) {
    const c = canvasRef.current, img = imgRef.current;
    if (!c || !img) return;
    c.width = img.naturalWidth; c.height = img.naturalHeight;
    const g = c.getContext('2d')!;
    g.drawImage(img, 0, 0);
    for (const a of [...list, ...(extra ? [extra] : [])]) paint(g, a, c.width);
  }
  function paint(g: CanvasRenderingContext2D, a: Annotation, W: number) {
    const lw = Math.max(2, Math.round(W / 400));
    g.lineWidth = lw; g.strokeStyle = a.color ?? '#e53935'; g.fillStyle = a.color ?? '#e53935';
    if (a.type === 'box') g.strokeRect(a.x, a.y, a.w ?? 0, a.h ?? 0);
    if (a.type === 'mask') { g.fillStyle = '#111'; g.fillRect(a.x, a.y, a.w ?? 0, a.h ?? 0); }
    if (a.type === 'arrow') {
      const x2 = a.x2 ?? a.x, y2 = a.y2 ?? a.y;
      g.beginPath(); g.moveTo(a.x, a.y); g.lineTo(x2, y2); g.stroke();
      const ang = Math.atan2(y2 - a.y, x2 - a.x), h = lw * 5;
      g.beginPath(); g.moveTo(x2, y2); g.lineTo(x2 - h * Math.cos(ang - 0.4), y2 - h * Math.sin(ang - 0.4)); g.lineTo(x2 - h * Math.cos(ang + 0.4), y2 - h * Math.sin(ang + 0.4)); g.closePath(); g.fill();
    }
    if (a.type === 'text' && a.text) {
      const fs = Math.max(14, Math.round(W / 50)); g.font = `bold ${fs}px sans-serif`;
      const tw = g.measureText(a.text).width;
      g.fillStyle = 'rgba(255,255,255,0.85)'; g.fillRect(a.x - 4, a.y - fs, tw + 8, fs + 8);
      g.fillStyle = a.color ?? '#e53935'; g.fillText(a.text, a.x, a.y);
    }
  }
  function pos(e: React.MouseEvent) {
    const c = canvasRef.current!; const r = c.getBoundingClientRect();
    return { x: Math.round((e.clientX - r.left) * (c.width / r.width)), y: Math.round((e.clientY - r.top) * (c.height / r.height)) };
  }
  function down(e: React.MouseEvent) {
    const p = pos(e);
    if (tool === 'text') { if (text.trim()) setAnns([...anns, { type: 'text', x: p.x, y: p.y, text: text.trim() }]); return; }
    setDrag(p);
  }
  function move(e: React.MouseEvent) {
    if (!drag) return; const p = pos(e);
    setCur(tool === 'arrow' ? { type: 'arrow', x: drag.x, y: drag.y, x2: p.x, y2: p.y } : { type: tool, x: Math.min(drag.x, p.x), y: Math.min(drag.y, p.y), w: Math.abs(p.x - drag.x), h: Math.abs(p.y - drag.y) });
  }
  function up() { if (cur && ((cur.w ?? 0) > 3 || (cur.h ?? 0) > 3 || cur.type === 'arrow')) setAnns([...anns, cur]); setCur(null); setDrag(null); }
  async function save() {
    setBusy(true); setErr('');
    try { draw(anns, null); const blob = await new Promise<Blob | null>((r) => canvasRef.current!.toBlob(r, 'image/png')); if (!blob) throw new Error('렌더 실패'); await onSave(anns, blob); } catch (e) { setErr((e as Error).message); } finally { setBusy(false); }
  }
  const tb = (t: Annotation['type'], label: string) => <button type="button" className={` px-2 py-1 text-xs border ${tool === t ? 'bg-accent text-white border-accent' : 'border-slate-300 hover:bg-slate-50'}`} onClick={() => setTool(t)}>{label}</button>;

  return (
    <div className="bg-white border border-slate-200 p-2">
      <div className="flex flex-wrap items-center gap-2 mb-2 text-sm">
        {tb('box', '박스')}{tb('arrow', '화살표')}{tb('mask', '마스킹')}{tb('text', '텍스트')}
        {tool === 'text' && <input className="border border-slate-300 px-2 py-1 text-xs w-48" placeholder="텍스트 입력 후 클릭" value={text} onChange={(e) => setText(e.target.value)} />}
        <button type="button" className="text-xs text-slate-600 hover:underline" onClick={() => setAnns(anns.slice(0, -1))} disabled={!anns.length}>되돌리기</button>
        <button type="button" className="text-xs text-slate-600 hover:underline" onClick={() => setAnns([])}>모두 지우기</button>
        <span className="flex-1" />
        <button type="button" className="border border-slate-300 px-3 py-1 text-xs hover:bg-slate-50" onClick={onCancel}>취소</button>
        <button type="button" className={buttonClass('primary')} disabled={busy} onClick={save}>적용본 저장</button>
      </div>
      {err && <div className="text-xs text-red-600 mb-1">{err}</div>}
      <canvas ref={canvasRef} className="max-w-full border cursor-crosshair" onMouseDown={down} onMouseMove={move} onMouseUp={up} onMouseLeave={() => { if (drag) up(); }} />
      <div className="text-xs text-slate-500 mt-1">원본은 그대로 보관되고, 적용본은 별도 객체로 저장됩니다. 마스킹은 불투명 검정으로 덮습니다.</div>
    </div>
  );
}
